import React, { useRef, useState, useEffect } from "react";

const BUTTON_WIDTH = 40; // approximate width of each Bootstrap page-link in px
const RESERVED_SLOTS = 5; // first page + last page + current page + 2 ellipses

/**
 * Pagination component showing page numbers with ellipsis.
 * Dynamically adjusts how many page numbers are visible based on container width.
 */
const Pagination = ({
  itemsCount = 1,
  pageSize,
  currentPage,
  onPageChange,
}) => {
  const navRef = useRef(null);
  const [spread, setSpread] = useState(1);
  const totalPages = Math.ceil(itemsCount / pageSize);

  useEffect(() => {
    const node = navRef.current;
    if (!node) return;

    const updateSpread = () => {
      const width = node.offsetWidth;
      const totalSlots = Math.floor(width / BUTTON_WIDTH);
      setSpread(Math.max(1, Math.floor((totalSlots - RESERVED_SLOTS) / 2)));
    };

    updateSpread();

    const observer = new ResizeObserver(updateSpread);
    observer.observe(node);
    return () => observer.disconnect();
  }, [totalPages]);

  // Don't render if only one page
  if (totalPages <= 1) return null;

  /**
   * Generate page numbers to display.
   * Always shows: 1, ..., [current - spread .. current + spread], ..., lastPage
   */
  const getPageNumbers = () => {
    const pages = [];

    // Always show first page
    pages.push(1);

    // Range around current page, clamped to [2, totalPages - 1]
    const rangeStart = Math.max(2, currentPage - spread);
    const rangeEnd = Math.min(totalPages - 1, currentPage + spread);

    // Add ellipsis after 1 if there's a gap
    if (rangeStart > 2) {
      pages.push("ellipsis-start");
    }

    // Add pages in range
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    // Add ellipsis before last if there's a gap
    if (rangeEnd < totalPages - 1) {
      pages.push("ellipsis-end");
    }

    // Always show last page (if more than 1 page)
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  // Brand color for active state
  const brandColor = "#385EA4";

  return (
    <nav
      ref={navRef}
      aria-label="Page navigation"
      style={{ width: "50%", margin: "0 auto" }}
    >
      <ul className="pagination justify-content-center mb-0">
        {pageNumbers.map((page) => {
          // Render ellipsis
          if (typeof page === "string" && page.startsWith("ellipsis")) {
            return (
              <li key={page} className="page-item disabled">
                <span
                  className="page-link"
                  style={{
                    outline: "none",
                    boxShadow: "none",
                    cursor: "default",
                  }}
                >
                  ...
                </span>
              </li>
            );
          }

          // Render page number
          const isActive = page === currentPage;
          return (
            <li key={page} className={`page-item ${isActive ? "active" : ""}`}>
              <button
                className="page-link"
                onClick={() => onPageChange(page)}
                aria-label={`Go to page ${page}`}
                aria-current={isActive ? "page" : undefined}
                style={{
                  outline: "none",
                  boxShadow: "none",
                  ...(isActive
                    ? {
                        backgroundColor: brandColor,
                        borderColor: brandColor,
                        color: "white",
                      }
                    : {}),
                }}
              >
                {page}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default Pagination;
